import {
  CORE_LEDGER_COLUMNS,
  type Ledger,
  type LedgerRow,
  type ResultSet,
} from '@projections/engine';

/**
 * Pure helpers that turn one path's engine `Ledger` into the row-and-column view the ledger table
 * renders (PLAN.md M7 screen 3 — "year-by-year ledger table"). Kept separate + pure so they unit-
 * test without React. The drill-down reads the SAME `Ledger` the engine produced (ledger-first
 * invariant), so a number on screen is exactly the simulator's number.
 */

/** The aggregate statement columns shown as the primary table (in bank-statement order). */
export const STATEMENT_COLUMNS = [
  'period',
  'startBalance',
  'income',
  'dividends',
  'expenses',
  'contributions',
  'withdrawals',
  'growth',
  'taxes',
  'endBalance',
] as const;
export type StatementColumn = (typeof STATEMENT_COLUMNS)[number];

/** Columns whose magnitude is an OUTFLOW (rendered with a leading −, neg tone). */
export const OUTFLOW_COLUMNS: ReadonlySet<string> = new Set(['expenses', 'withdrawals', 'taxes']);

export interface LedgerViewRow {
  /** The calendar year (the `period` column value). */
  readonly year: number;
  /** Every column for this year as a plain map (core + dynamic). */
  readonly cells: LedgerRow;
}

/** Project a `Ledger` into an array of per-year row maps (one object per simulated year). */
export function ledgerToRows(ledger: Ledger): LedgerViewRow[] {
  const rows: LedgerViewRow[] = [];
  for (let i = 0; i < ledger.length; i++) {
    const cells = ledger.row(i);
    rows.push({ year: cells.period, cells });
  }
  return rows;
}

/**
 * The dynamic (non-core) columns present on a ledger, e.g. `acct.*`, `cat.*`, `tax.*`. Excludes ALL
 * fixed core columns (base + enrichment aggregates like `dividends` / `realizedGainsSet` /
 * `shortfall` / `depleted`), which already render in the statement / `explain()` block — so the
 * structured drill-down only shows the per-account / per-category / per-tax detail.
 */
export function dynamicColumns(ledger: Ledger): string[] {
  const core = new Set<string>(CORE_LEDGER_COLUMNS);
  return ledger.columnNames().filter((n) => !core.has(n));
}

// ---------------------------------------------------------------------------
// Dynamic-column drill-down (grouping the per-account / per-category / per-tax
// columns for the expanded-row breakdown — pure, so it unit-tests without React)
// ---------------------------------------------------------------------------

/** One namespaced family of dynamic columns for a single year (e.g. all `acct.<id>.*`). */
export interface DynamicDrillGroup {
  /** Group key: `Accounts`, `Spending`, `Taxes`, or the raw namespace for anything else. */
  readonly title: string;
  /** Each labelled line with its value for the year, in column order. */
  readonly lines: ReadonlyArray<{ readonly label: string; readonly value: number }>;
}

/** Prettify a dynamic column name for the breakdown (`acct.rmf.balance` → `rmf · balance`). */
export function prettyDynamicColumn(name: string): string {
  if (name.startsWith('acct.')) {
    const rest = name.slice('acct.'.length);
    const dot = rest.lastIndexOf('.');
    if (dot > 0) return `${rest.slice(0, dot)} · ${rest.slice(dot + 1)}`;
    return rest;
  }
  if (name.startsWith('cat.')) return name.slice('cat.'.length);
  if (name.startsWith('tax.')) return name.slice('tax.'.length);
  return name;
}

/**
 * Group a year's dynamic columns into the breakdown families the expanded row renders. Accounts
 * (`acct.*`), spending (`cat.*`), and taxes (`tax.*`) get friendly titles; anything else falls
 * into its raw namespace. Returns only non-empty groups, in a stable display order.
 */
export function drillGroups(ledger: Ledger, year: number): DynamicDrillGroup[] {
  const index = ledger.indexOfPeriod(year);
  if (index === -1) return [];
  const row = ledger.row(index);
  const buckets = new Map<string, Array<{ label: string; value: number }>>();
  const titleFor = (name: string): string => {
    if (name.startsWith('acct.')) return 'Accounts';
    if (name.startsWith('cat.')) return 'Spending';
    if (name.startsWith('tax.')) return 'Taxes';
    return name.split('.')[0] ?? name;
  };
  for (const name of dynamicColumns(ledger)) {
    const title = titleFor(name);
    const lines = buckets.get(title) ?? [];
    lines.push({ label: prettyDynamicColumn(name), value: row[name] ?? 0 });
    buckets.set(title, lines);
  }
  const order = ['Accounts', 'Spending', 'Taxes'];
  const titles = [...buckets.keys()].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
  });
  return titles.map((title) => ({ title, lines: buckets.get(title) ?? [] }));
}

// ---------------------------------------------------------------------------
// Path selection — pick WHICH path's ledger to read (pure, ledger-first)
// ---------------------------------------------------------------------------

/** The kind of a path shortcut, used to label + pick a representative path. */
export type PathShortcutKind = 'p5' | 'p25' | 'median' | 'p75' | 'p95' | 'worst';

/** A pickable path: its index into `result.ledgers`, terminal wealth, and whether it depleted. */
export interface PathOption {
  readonly pathIndex: number;
  readonly terminalWealth: number;
  readonly depleted: boolean;
}

/** A path's terminal (final-year) end balance, or 0 for an empty ledger. */
export function pathTerminalWealth(ledger: Ledger): number {
  return ledger.length > 0 ? ledger.get(ledger.length - 1, 'endBalance') : 0;
}

/** Whether a path ever depleted (the floor-at-zero failure flag, in ANY year). */
export function pathDepleted(ledger: Ledger): boolean {
  if (!ledger.hasColumn('depleted')) return false;
  for (let i = 0; i < ledger.length; i++) {
    if (ledger.get(i, 'depleted') > 0.5) return true;
  }
  return false;
}

/**
 * Rank every path index by terminal end balance ascending (poorest outcome first). Ties keep the
 * original (lower) index for stability. This is the ordering the percentile shortcuts read off, so
 * "median path" means the path whose terminal wealth sits at the 50th percentile of all paths.
 */
export function rankPathsByTerminal(result: ResultSet): number[] {
  const indices = result.ledgers.map((_, i) => i);
  return indices.sort((a, b) => {
    const ta = pathTerminalWealth(result.ledgers[a] as Ledger);
    const tb = pathTerminalWealth(result.ledgers[b] as Ledger);
    return ta === tb ? a - b : ta - tb;
  });
}

/**
 * The path index sitting at `fraction` (0..1) of the terminal-wealth ranking. `fraction = 0.5`
 * → the median path. Uses nearest-rank on the sorted indices (the same intent as a percentile
 * pick, but resolving to a CONCRETE path so its full ledger can be read). Returns 0 when empty.
 */
export function pathAtPercentile(result: ResultSet, fraction: number): number {
  const ranked = rankPathsByTerminal(result);
  if (ranked.length === 0) return 0;
  const clamped = Math.min(1, Math.max(0, fraction));
  const pos = Math.round(clamped * (ranked.length - 1));
  return ranked[pos] ?? 0;
}

/**
 * The most useful path to debug a failure: the FIRST path that depleted (ledger order), else the
 * poorest by terminal wealth. Returns 0 when there are no paths. (Mirrors the engine's
 * `failingPathIndex` intent for the drill-down: "show me what went wrong".)
 */
export function worstPathIndex(result: ResultSet): number {
  for (let i = 0; i < result.ledgers.length; i++) {
    if (pathDepleted(result.ledgers[i] as Ledger)) return i;
  }
  return pathAtPercentile(result, 0);
}

/** Resolve a shortcut to a concrete path index against the result's terminal-wealth ranking. */
export function pathForShortcut(result: ResultSet, kind: PathShortcutKind): number {
  switch (kind) {
    case 'p5':
      return pathAtPercentile(result, 0.05);
    case 'p25':
      return pathAtPercentile(result, 0.25);
    case 'median':
      return pathAtPercentile(result, 0.5);
    case 'p75':
      return pathAtPercentile(result, 0.75);
    case 'p95':
      return pathAtPercentile(result, 0.95);
    case 'worst':
      return worstPathIndex(result);
    default: {
      const _exhaustive: never = kind;
      throw new Error(`unknown shortcut ${String(_exhaustive)}`);
    }
  }
}

/** Describe a path index (terminal wealth + depleted flag) for labelling the picker. */
export function describePath(result: ResultSet, pathIndex: number): PathOption {
  const ledger = result.ledgers[pathIndex];
  if (ledger === undefined) {
    return { pathIndex, terminalWealth: 0, depleted: false };
  }
  return {
    pathIndex,
    terminalWealth: pathTerminalWealth(ledger),
    depleted: pathDepleted(ledger),
  };
}
